import getRemoteMetricKey from 'common/src/modules/analytics/utils/getRemoteMetricKey';
import {AvailableDimension} from 'common/src/modules/campaigns/store/insightsPage/insightsPage.types';
import dashboardsApi from 'common/src/modules/dashboards/dashboards.api';
import {
  AiDashboardWidgetType,
  DashboardWidgetType,
} from 'common/src/modules/dashboards/dashboards.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import _find from 'lodash/find';
import _flatten from 'lodash/flatten';
import _map from 'lodash/map';
import {
  AiAnalyticsEventType,
  AnalyticsEventType,
  DimensionValue,
  MetricValue,
} from '../../../../analytics.types';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import {copilotAnalyticsWidgetSchema} from '../../../copilotAnalytics.schemas';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';
import partitionByEventType from '../utils/partitionByEventType';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';

function checkAccountWidgetExistsTool(store: CLStore) {
  async function execute({
    input: {dimension: aiDimension, metrics: aiMetrics, breakdownByTime, type, eventType},
  }: ToolExecuteParams<typeof copilotAnalyticsWidgetSchema>): Promise<ToolResult> {
    const {
      dashboardPage: {dashboards, metricsAndDimensionsOptions},
    } = store.getState();

    const widgetType = getMatchingEnumValue(AiDashboardWidgetType, DashboardWidgetType, type);
    const selectedEventType = getMatchingEnumValue(
      AiAnalyticsEventType,
      AnalyticsEventType,
      eventType,
    );

    const [dimensionsByEventType] = partitionByEventType(
      metricsAndDimensionsOptions.dimensions,
      selectedEventType,
    ) as unknown as AvailableDimension[][];

    const dimension =
      getRemoteDimensionKey<DimensionValue>(dimensionsByEventType, aiDimension) ??
      DimensionValue.Date;

    const metrics = _map(aiMetrics, (metric) =>
      getRemoteMetricKey(metricsAndDimensionsOptions.metrics, metric),
    ) as MetricValue[];

    const widgets = await Promise.all(_map(dashboards, ({id}) => dashboardsApi.getWidgets(id)));

    const existingWidget = _find(_flatten(widgets), {
      dimensions: [dimension],
      metrics,
      type: widgetType,
      breakdownByTime,
      eventType: selectedEventType,
    });

    const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);

    if (isSkillsMode) {
      if (existingWidget) {
        return toolResult(
          JSON.stringify({dashboardId: existingWidget?.dashboardId, widgetId: existingWidget?.id}),
        );
      }

      return toolResult(JSON.stringify({dashboardId: null, widgetId: null}));
    }

    if (existingWidget) {
      return toolResult(
        `Inline-widget exists in dashboard id: ${existingWidget?.dashboardId}, widget id: ${existingWidget?.id}`,
      );
    }

    return toolResult('Inline-widget does not exist');
  }

  return tool({
    name: AccountAnalyticsToolName.CheckAccountWidgetExists,
    displayName: 'Checking if widget exists',
    description:
      'check if a widget with the specific properties already exists in account dashboard',
    execute,
    inputSchema: copilotAnalyticsWidgetSchema,
  });
}

export default checkAccountWidgetExistsTool;
